import { useState } from "react";
import { ArrowRight, Loader2, ShieldCheck, Clock4, Mail } from "lucide-react";
import { toast } from "sonner";
import { submitRFQ } from "@/lib/api";

const CATEGORIES = [
  "Machined Components",
  "Forged Parts",
  "Welded Assemblies",
  "Sheet Metal",
  "Castings",
  "Plated / Painted Parts",
  "Sub-Assemblies",
  "Fasteners & Hardware",
  "Electrical / Switchgear",
  "Other",
];

const COUNTRIES = [
  "Germany",
  "United Kingdom",
  "United States",
  "United Arab Emirates",
  "France",
  "Italy",
  "Spain",
  "Netherlands",
  "Saudi Arabia",
  "Other",
];

const initial = {
  country: "",
  company: "",
  designation: "",
  department: "",
  contact_name: "",
  email: "",
  phone: "",
  category: "",
  quantity: "",
  delivery: "",
  message: "",
};

export default function RFQForm() {
  const [form, setForm] = useState(initial);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const update = (k) => (e) => setForm((s) => ({ ...s, [k]: e.target.value }));

  const onSubmit = async (e) => {
    e.preventDefault();
    if (
      !form.country ||
      !form.company ||
      !form.contact_name ||
      !form.email ||
      !form.phone ||
      !form.category
    ) {
      toast.error("Please complete all required fields.");
      return;
    }
    try {
      setLoading(true);
      await submitRFQ(form);
      setDone(true);
      setForm(initial);
      toast.success("RFQ received. Our team will respond to you quickly.");
    } catch (err) {
      console.error(err);
      toast.error("Could not submit. Please try again or email us directly.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <section
      id="rfq"
      data-testid="rfq-section"
      className="relative py-24 lg:py-32 bg-brand-navy text-white overflow-hidden"
    >
      <div className="absolute inset-0 bp-grid opacity-25 pointer-events-none" />
      <div className="absolute -top-24 right-0 h-80 w-80 rounded-full bg-brand-orange/15 blur-3xl pointer-events-none" />
      <div className="absolute -bottom-24 left-0 h-80 w-80 rounded-full bg-brand-earth/15 blur-3xl pointer-events-none" />

      <div className="container-x relative">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-5">
              <span className="divider-rule" />
              <span className="eyebrow !text-white/80">Request a Quote</span>
            </div>
            <h2 className="font-heading font-extrabold text-3xl sm:text-4xl lg:text-5xl text-white tracking-tight leading-[1.05]">
              Get a Quote
              <br />
              <span className="text-brand-orange">Quickly.</span>
            </h2>
            <p className="mt-6 text-white/80 text-base lg:text-lg leading-relaxed max-w-md">
              Share your drawing, target quantity and delivery destination. Our
              engineers respond quickly with a transparent technical and
              commercial proposal.
            </p>

            <div className="mt-10 space-y-5">
              {[
                { icon: Clock4, t: "Quick engineer response", s: "Direct from our technical desk" },
                { icon: ShieldCheck, t: "NDA-protected enquiries", s: "Your drawings stay confidential" },
                { icon: Mail, t: "marketing@tirupatigroups.co.in", s: "Or email us directly" },
              ].map((b) => {
                const Icon = b.icon;
                return (
                  <div key={b.t} className="flex items-start gap-4">
                    <div className="h-10 w-10 flex items-center justify-center bg-white/10 border border-white/15">
                      <Icon className="h-4 w-4 text-brand-orange" />
                    </div>
                    <div>
                      <div className="font-semibold">{b.t}</div>
                      <div className="text-sm text-white/65">{b.s}</div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>

          <div className="lg:col-span-7">
            <form
              onSubmit={onSubmit}
              noValidate
              data-testid="rfq-form"
              className="bg-white text-brand-text p-8 lg:p-10 relative"
            >
              {done && (
                <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/95 backdrop-blur">
                  <div className="text-center max-w-md px-6">
                    <div className="mx-auto h-14 w-14 flex items-center justify-center bg-brand-green text-white">
                      <ShieldCheck className="h-6 w-6" />
                    </div>
                    <h3 className="mt-6 font-heading font-extrabold text-2xl text-brand-navy">
                      RFQ Received
                    </h3>
                    <p className="mt-3 text-brand-text/70">
                      Thank you. Our engineering team will respond to you
                      quickly with a tailored proposal.
                    </p>
                    <button
                      type="button"
                      data-testid="rfq-new"
                      onClick={() => setDone(false)}
                      className="btn-secondary mt-7"
                    >
                      Submit another enquiry
                    </button>
                  </div>
                </div>
              )}

              <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                <Field label="Country *">
                  <select
                    data-testid="rfq-country"
                    value={form.country}
                    onChange={update("country")}
                    className="input"
                    required
                  >
                    <option value="">Select country</option>
                    {COUNTRIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Company *">
                  <input
                    data-testid="rfq-company"
                    type="text"
                    value={form.company}
                    onChange={update("company")}
                    className="input"
                    placeholder="Company name"
                    required
                  />
                </Field>
                <Field label="Designation">
                  <input
                    data-testid="rfq-designation"
                    type="text"
                    value={form.designation}
                    onChange={update("designation")}
                    className="input"
                    placeholder="e.g. Procurement Manager"
                  />
                </Field>
                <Field label="Department">
                  <input
                    data-testid="rfq-department"
                    type="text"
                    value={form.department}
                    onChange={update("department")}
                    className="input"
                    placeholder="e.g. Purchasing / Engineering"
                  />
                </Field>
                <Field label="Contact Name *">
                  <input
                    data-testid="rfq-contact"
                    type="text"
                    value={form.contact_name}
                    onChange={update("contact_name")}
                    className="input"
                    placeholder="Full name"
                    required
                  />
                </Field>
                <Field label="Email *">
                  <input
                    data-testid="rfq-email"
                    type="email"
                    value={form.email}
                    onChange={update("email")}
                    className="input"
                    placeholder="name@company.com"
                    required
                  />
                </Field>
                <Field label="Phone Number *" className="md:col-span-2">
                  <input
                    data-testid="rfq-phone"
                    type="tel"
                    value={form.phone}
                    onChange={update("phone")}
                    className="input"
                    placeholder="+ country code ..."
                    required
                  />
                </Field>

                <Field label="Product Category *">
                  <select
                    data-testid="rfq-category"
                    value={form.category}
                    onChange={update("category")}
                    className="input"
                    required
                  >
                    <option value="">Select category</option>
                    {CATEGORIES.map((c) => (
                      <option key={c} value={c}>
                        {c}
                      </option>
                    ))}
                  </select>
                </Field>
                <Field label="Estimated Quantity Required">
                  <input
                    data-testid="rfq-quantity"
                    type="text"
                    value={form.quantity}
                    onChange={update("quantity")}
                    className="input"
                    placeholder="e.g. 5,000 pcs / year"
                  />
                </Field>
                <Field label="Delivery" className="md:col-span-2">
                  <input
                    data-testid="rfq-delivery"
                    type="text"
                    value={form.delivery}
                    onChange={update("delivery")}
                    className="input"
                    placeholder="Required delivery location / timeline"
                  />
                </Field>
              </div>

              <Field label="Project Details / Message" className="mt-5">
                <textarea
                  data-testid="rfq-message"
                  value={form.message}
                  onChange={update("message")}
                  rows={5}
                  className="input resize-none"
                  placeholder="Tell us about your part, drawing reference, target price, lead time…"
                />
              </Field>

              <div className="mt-7 flex flex-wrap items-center gap-5 justify-between">
                <div className="text-xs text-brand-text/55">
                  By submitting you agree to be contacted regarding your enquiry.
                  We respect your confidentiality.
                </div>
                <button
                  data-testid="rfq-submit"
                  type="submit"
                  disabled={loading}
                  className="btn-primary !px-8 disabled:opacity-70"
                >
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 animate-spin" /> Submitting…
                    </>
                  ) : (
                    <>
                      Get Quote — Quickly <ArrowRight className="h-4 w-4" />
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>

      <style>{`
        .input {
          width: 100%;
          background: #F8FAFC;
          border: 1px solid #E5E7EB;
          padding: 0.75rem 0.9rem;
          font-size: 0.9rem;
          color: #1F2937;
          outline: none;
          transition: border-color 0.2s, background 0.2s;
        }
        .input:focus {
          border-color: #0B2E59;
          background: #FFFFFF;
        }
        .input::placeholder { color: #9CA3AF; }
      `}</style>
    </section>
  );
}

function Field({ label, children, className = "" }) {
  return (
    <label className={`block ${className}`}>
      <span className="text-xs uppercase tracking-[0.18em] text-brand-text/60 font-semibold">
        {label}
      </span>
      <div className="mt-1.5">{children}</div>
    </label>
  );
}
